<template>
  <!-- The loader appears at once after `delay`; only the exit fades. -->
  <div
    v-if="visible"
    ref="root"
    class="alice-loader"
    :class="{ 'is-done': fading }"
    role="status"
    aria-live="polite"
  >
    <div class="alice-loader__grid" aria-hidden="true">
      <div
        v-for="(i, n) in cells"
        :key="n"
        class="alice-loader__cell"
        :style="{ '--i': i }"
      />
    </div>
    <div class="alice-loader__status">{{ status }}</div>
  </div>
</template>

<script>
// 40 cells, 8 per row. --i = row + column, so the wave sweeps diagonally.
const CELLS = Array.from({ length: 40 }, (_, n) => Math.floor(n / 8) + (n % 8));
// Keep in sync with the opacity transition in the styles below (--dur-base).
const FADE_MS = 200;

export default {
  name: "AliceLoader",
  props: {
    // Whether something is loading. The loader only paints after `delay`.
    modelValue: { type: Boolean, default: false },
    status: { type: String, default: "Just a moment" },
    // Wait this long before showing, so short requests don't flash a loader.
    delay: { type: Number, default: 300 },
  },
  data() {
    return { visible: false, fading: false, showTimer: null, fadeTimer: null };
  },
  computed: {
    cells: () => CELLS,
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(loading) {
        clearTimeout(this.showTimer);
        clearTimeout(this.fadeTimer);
        if (loading) {
          // Still on screen mid-fade → fade it back in instead of remounting.
          this.fading = false;
          if (this.visible) return;
          if (this.delay === 0) {
            this.visible = true;
            return;
          }
          this.showTimer = setTimeout(() => {
            this.visible = true;
          }, this.delay);
          return;
        }
        if (!this.visible) return;
        // The delay timer can fire in the same tick that loading ends, before
        // Vue has rendered. Nothing is on screen yet, so drop it — don't fade.
        if (!this.$refs.root) {
          this.visible = false;
          return;
        }
        this.fading = true;
        this.fadeTimer = setTimeout(() => {
          this.visible = false;
          this.fading = false;
        }, FADE_MS);
      },
    },
  },
  beforeUnmount() {
    clearTimeout(this.showTimer);
    clearTimeout(this.fadeTimer);
  },
};
</script>

<style scoped>
.alice-loader {
  /* Half the handoff's 14px/7px grid — integers, so the cells stay crisp. */
  --cell: 7px;
  --cell-gap: 3px;
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  /* Tighter than the handoff's 28px — that spacing carried the wordmark too. */
  gap: var(--space-4);
  /* Translucent veil, not a wall — the page stays vaguely readable behind it. */
  background: rgba(255, 255, 255, 0.86);
  -webkit-backdrop-filter: blur(3px);
  backdrop-filter: blur(3px);
  transition: opacity var(--dur-base) var(--ease-out);
}
.alice-loader.is-done {
  opacity: 0;
  pointer-events: none;
}

.alice-loader__grid {
  display: grid;
  grid-template-columns: repeat(8, var(--cell));
  gap: var(--cell-gap);
}
.alice-loader__cell {
  width: var(--cell);
  height: var(--cell);
  background: var(--vp-black);
  animation: alice-wave 2.4s ease-in-out infinite;
  animation-delay: calc(var(--i) * 0.09s);
}
@keyframes alice-wave {
  0%,
  100% {
    opacity: 0.12;
  }
  40% {
    opacity: 1;
  }
}

.alice-loader__status {
  font-family: var(--font-body);
  font-weight: 500;
  font-size: 12px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--vp-grijs-120);
}

/* Mobile: 6 columns, last row dropped. */
@media (max-width: 480px) {
  .alice-loader__grid {
    grid-template-columns: repeat(6, var(--cell));
  }
  .alice-loader__cell:nth-child(n + 37) {
    display: none;
  }
}

/* No motion → a static grid instead of a frozen keyframe. */
@media (prefers-reduced-motion: reduce) {
  .alice-loader__cell {
    animation: none;
    opacity: 0.55;
  }
  .alice-loader {
    transition: none;
  }
}
</style>
